import { useEffect, useState } from "react";
import {
  AUTH_SESSION_CHANGED_EVENT,
  readAuthToken,
  tryBootstrapPortalSession,
} from "@/shared/utils/authSession.util";
import { useAuthUiStore } from "@/state/stores/authUiStore";

/**
 * Portal client auth. Token is kept in React state and synced when login/logout
 * updates the cookie so route/layout trees re-render without a full page reload.
 * Bootstraps silently from HttpOnly refresh when access JWT is missing/expired.
 * Bootstrap flags also mirror into Zustand `authUiStore` for non-React consumers.
 */
export function useAuth() {
  const [hasMounted, setHasMounted] = useState(false);
  const [token, setToken] = useState<string | null>(() => readAuthToken());
  const [bootstrapping, setBootstrapping] = useState(true);

  useEffect(() => {
    setHasMounted(true);
    useAuthUiStore.getState().setHasMounted(true);
    let cancelled = false;
    (async () => {
      try {
        await tryBootstrapPortalSession();
      } finally {
        if (!cancelled) {
          setToken(readAuthToken());
          setBootstrapping(false);
          useAuthUiStore.getState().setBootstrapping(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const syncToken = () => setToken(readAuthToken());
    syncToken();
    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, syncToken);
    return () => window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, syncToken);
  }, []);

  return {
    token,
    isAuthenticated: Boolean(token),
    /** Avoid treating first paint + silent refresh as logged out. */
    isLoading: !hasMounted || bootstrapping,
  };
}
